import React from "react";
import { Box, Tab, Tabs as MUITabs, Tooltip } from "@mui/material";
import { Warning } from "@mui/icons-material";
import IyugaTooltip from "../../../IyugaTooltip";

const Tabs = ({
  selectedTab,
  setSelectedTab,
  tabList,
  orientation = "vertical",
  borderTop = false,
  // T22.576 (Gordon S23+ Chrome, Themes popup): opt-in for the horizontal
  // strip. The default scrollable strip (QA-1191) kept every label whole but
  // at a 384px phone the three Themes tabs (434px) overflowed a 279px
  // scroller: "Business Bo..." cut by the scroll edge and LinkVault off-screen
  // entirely. With fitNarrow every tab shares the row equally (icon stacked
  // above the label, label may wrap BETWEEN words), so all tabs are always
  // fully visible. minWidth stays "min-content": a tab is never narrower than
  // its longest word, so the QA-1191 mid-word clip can't come back.
  fitNarrow = false,
  tabErrors = {}, // Object containing error information for each tab
}) => {
  const fit = fitNarrow && orientation === "horizontal";
  const tabStyle = (value) => ({
    color: selectedTab === value ? "#8126DB" : "#00000099",
    fontSize: "14px",
    lineHeight: "20px",
    marginBottom: "10px",
    borderRadius: "4px",
    textTransform: "capitalize",
    // batch-proof-fe-778bf9f4 (2026-09-19): a border of "none" (0px) on the
    // unselected state vs "3px solid" on the selected state changes how much
    // width the selection indicator eats out of the tab's own content box,
    // so the SELECTED tab's icon+label sits 3px further right than its
    // unselected siblings (live-measured on web-dev.iyuga.net: Business Box/
    // LinkVault icon left edge x=229, Gallery Theme (default-selected) x=232).
    // Reserve the same 3px always via a transparent border when unselected,
    // so only the colour changes on selection, never the content's position.
    borderLeft: !borderTop
      ? `3px solid ${selectedTab === value ? "#8126DB" : "transparent"}`
      : "none",
    borderTop: borderTop
      ? `3px solid ${selectedTab === value ? "#8126DB" : "transparent"}`
      : "none",
    backgroundColor: selectedTab === value ? "#EFE9FE" : "#F6F7F9",
    fontWeight: "700",
    display: "flex",
    justifyContent: "space-evenly",
    // QA-1191: was `minWidth: 0`, which let a tab shrink below its label and
    // clip it ("LinkVault" -> ".inkVau", "Gallery Theme" -> "Galler Them" at
    // 768px). Never render a tab narrower than its label; keep the label on one
    // line. Overflow is handled by the scrollable strip in horizontal mode.
    // QA-1191: was `minWidth: 0`, which let a tab shrink below its label and
    // clip it ("LinkVault" -> ".inkVau", "Gallery Theme" -> "Galler Them" at
    // 768px). Never render a tab narrower than its label; keep the label on one
    // line. Overflow is handled by the scrollable strip in horizontal mode.
    minWidth: "max-content",
    whiteSpace: "nowrap",
    // Vertical rail (>=md): each tab fills the column equally. Horizontal
    // reflow (narrow widths): size to content and let the scrollable strip
    // scroll rather than squish/clip.
    ...(orientation === "horizontal" ? {} : { flex: 1 }),
    ...(fit
      ? {
          flex: "1 1 0",
          minWidth: "min-content",
          whiteSpace: "normal",
          padding: "8px 6px",
          marginBottom: 0,
        }
      : {}),
  });

  const handleTabChange = (e, newVal) => {
    setSelectedTab(newVal);
  };

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        display: "flex",
        padding: orientation === "vertical" && "0px 20px",
        width: "100%",
      }}
    >
      <MUITabs
        orientation={orientation}
        // QA-1191: in the horizontal (narrow) reflow the tabs may not all fit
        // across the modal width — scroll them instead of squishing/clipping.
        // Vertical rail keeps equal fullWidth tabs (>=md, unchanged).
        variant={orientation === "horizontal" && !fit ? "scrollable" : "fullWidth"}
        scrollButtons={orientation === "horizontal" && !fit ? "auto" : false}
        allowScrollButtonsMobile
        value={selectedTab}
        onChange={handleTabChange}
        aria-label="Gallery settings tabs"
        TabIndicatorProps={{ style: { display: "none" } }}
        sx={{
          width: "100%",
          "& .MuiTabs-flexContainer": {
            width: "100%",
            display: "flex",
            gap: orientation === "horizontal" ? (fit ? "6px" : "10px") : 0, // Add gap using flexbox
            justifyContent: "space-between",
          },
        }}
      >
        {tabList.map(({ label, value, icon: Icon }) => (
          <Tab
            key={value}
            label={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  ...(fit
                    ? { flexDirection: "column", textAlign: "center", lineHeight: "16px" }
                    : {}),
                  // Gordon 2026-09-18 (gordon-0918-themes-modal-...-
                  // linkvault-bb, item 1 correction): was "center". Equal
                  // icon boxes alone don't produce equal left edges when
                  // labels differ in length ("Gallery Theme" vs "LinkVault")
                  // -- centering the icon+label GROUP shifts a shorter row's
                  // icon further right than a longer row's, which is exactly
                  // Gordon's "rows are not aligned" complaint. Left-justify
                  // so every row's icon starts at the same x regardless of
                  // its label's width. No visual change in the horizontal
                  // (mobile) reflow: those tabs are content-sized
                  // (minWidth:"max-content"), so there's no extra space to
                  // redistribute either way.
                  justifyContent: fit ? "center" : "flex-start",
                  gap: fit ? "4px" : "6px", // Add gap between elements
                }}
              >
                {Icon && (
                  // Gordon 2026-09-18 (gordon-0918-themes-modal-side-pane-
                  // icons-accent-borders-corners-linkvault-bb): a fixed-size
                  // slot around the icon, not just an sx prop on the icon
                  // itself -- sx only sizes a real MUI SvgIcon; a plain
                  // imported <svg> (e.g. a ReactComponent asset) ignores sx
                  // entirely and keeps its own file's width/height, which
                  // threw LinkVault's tab icon out of alignment with the
                  // other two (see ThemesPopup.jsx's file banner). The `&
                  // svg` rule forces ANY icon's rendered <svg> to fill this
                  // 16x16 box via real CSS (which always wins over an SVG's
                  // own width/height presentation attributes), so every tab
                  // icon gets the same box size and left edge regardless of
                  // which icon implementation it is.
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "& svg": { width: "100%", height: "100%" },
                    }}
                  >
                    <Icon sx={{ fontSize: 16, width: 16, height: 16 }} />
                  </Box>
                )}
                <span>{label}</span>
                {tabErrors[value] && (
                  <IyugaTooltip
                    placement="top"
                    tooltip="Some fields are missing or invalid"
                  >
                    <Warning
                      sx={{
                        fontSize: 16,
                        width: 16,
                        height: 16,
                        color: "#f57c00",
                        ml: "2px",
                      }}
                    />
                  </IyugaTooltip>
                )}
              </Box>
            }
            value={value}
            style={tabStyle(value)}
          />
        ))}
      </MUITabs>
    </Box>
  );
};

export default Tabs;
